// Site header navigation (templates/_header.twig, ADR 0009). Disclosure pattern: buttons with aria-expanded toggle
// panels anchored under their own item. Hover intent on desktop pointers, click/Enter/Space everywhere, Esc to close.
// Below the nav breakpoint the nav becomes a drawer opened by the menu toggle, and panels act as accordions.

const DESKTOP = '(min-width: 1200px)'; // keep in sync with $break-nav in ds/_nav.scss
const HOVER = '(hover: hover) and (pointer: fine)';
const OPEN_DELAY = 80;
const CLOSE_DELAY = 250;
const EDGE = 16;

class SiteNav {
    constructor(header) {
        this.header = header;
        this.nav = header.querySelector('[data-site-nav]');
        this.toggle = header.querySelector('[data-nav-toggle]');
        this.items = [...header.querySelectorAll('[data-nav-item]')].map((el) => ({
            el,
            trigger: el.querySelector('[data-nav-trigger]'),
            panel: el.querySelector('[data-nav-panel]'),
            timer: null,
        }));
        this.desktop = window.matchMedia(DESKTOP);
        this.hover = window.matchMedia(HOVER);
        this.bind();
        this.observeScroll();
    }

    bind() {
        this.items.forEach((item) => {
            item.trigger.addEventListener('click', () => {
                this.isOpen(item) ? this.close(item) : this.open(item);
            });

            item.el.addEventListener('pointerenter', (e) => {
                if (!this.canHover(e)) return;
                clearTimeout(item.timer);
                item.timer = setTimeout(() => this.open(item), OPEN_DELAY);
            });
            item.el.addEventListener('pointerleave', (e) => {
                if (!this.canHover(e)) return;
                clearTimeout(item.timer);
                item.timer = setTimeout(() => this.close(item), CLOSE_DELAY);
            });

            // Close when focus leaves the item (desktop only; the drawer keeps accordions open).
            item.el.addEventListener('focusout', (e) => {
                if (this.desktop.matches && !item.el.contains(e.relatedTarget)) this.close(item);
            });
        });

        this.toggle?.addEventListener('click', () => this.setDrawer(!this.drawerOpen()));

        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            const open = this.items.find((item) => this.isOpen(item));
            if (open && (this.desktop.matches || !this.drawerOpen() || open.el.contains(document.activeElement))) {
                this.close(open);
                open.trigger.focus();
            } else if (this.drawerOpen()) {
                this.setDrawer(false);
                this.toggle.focus();
            }
        });

        document.addEventListener('click', (e) => {
            if (this.desktop.matches && !this.nav.contains(e.target)) this.closeAll();
        });

        this.desktop.addEventListener('change', () => {
            this.closeAll();
            this.setDrawer(false);
        });

        window.addEventListener('resize', () => {
            this.items.filter((item) => this.isOpen(item)).forEach((item) => this.position(item));
        }, { passive: true });
    }

    canHover(e) {
        return e.pointerType === 'mouse' && this.hover.matches && this.desktop.matches;
    }

    isOpen(item) {
        return item.trigger.getAttribute('aria-expanded') === 'true';
    }

    open(item) {
        if (this.isOpen(item)) return;
        if (this.desktop.matches) this.items.filter((other) => other !== item).forEach((other) => this.close(other));
        item.trigger.setAttribute('aria-expanded', 'true');
        item.panel.hidden = false;
        item.el.classList.add('is-open');
        this.position(item);
    }

    close(item) {
        clearTimeout(item.timer);
        if (!this.isOpen(item)) return;
        item.trigger.setAttribute('aria-expanded', 'false');
        item.panel.hidden = true;
        item.el.classList.remove('is-open');
    }

    closeAll() {
        this.items.forEach((item) => this.close(item));
    }

    // Panels hang from their trigger's left edge; shift them back inside the viewport when they'd overflow.
    position(item) {
        item.panel.style.removeProperty('--shift');
        if (!this.desktop.matches) return;
        const rect = item.panel.getBoundingClientRect();
        const max = document.documentElement.clientWidth - EDGE;
        let shift = 0;
        if (rect.right > max) shift = max - rect.right;
        if (rect.left + shift < EDGE) shift = EDGE - rect.left;
        if (shift) item.panel.style.setProperty('--shift', `${Math.round(shift)}px`);
    }

    drawerOpen() {
        return this.toggle?.getAttribute('aria-expanded') === 'true';
    }

    setDrawer(open) {
        if (!this.toggle) return;
        this.toggle.setAttribute('aria-expanded', String(open));
        this.header.classList.toggle('is-drawer-open', open);
        document.documentElement.classList.toggle('nav-locked', open);
        if (!open) this.closeAll();
    }

    // Shadow once the page scrolls under the header.
    observeScroll() {
        const sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none';
        document.body.prepend(sentinel);
        new IntersectionObserver(([entry]) => {
            this.header.classList.toggle('is-scrolled', !entry.isIntersecting);
        }).observe(sentinel);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('[data-site-header]');
    if (header) window.wghi.siteNav = new SiteNav(header);
});
